<template>
    <div class="radio-group-wrapper" :class="{ 'radio-group-stacked': stacked }">
        <div
            class="radio-group"
            :class="{ 'radio-group-stacked': stacked }"
            data-tooltip-anchor="group"
        >
            <div
                v-for="option in options"
                :key="option.value"
                class="form-check d-flex align-items-center"
                data-tooltip-anchor-item
            >
                <input
                    class="form-check-input"
                    :class="{ 'input-invalid': isInvalid }"
                    :style="{ cursor: 'pointer' }"
                    type="radio"
                    :name="name"
                    :value="option.value"
                    v-model="localValue"
                    :disabled="option?.disabled || disabled"
                    @change="localValueChange"
                    :required="required"
                    show
                    :id="`${name}-${option.value}`"
                    v-on:invalid.prevent="handleInvalid"
                    v-on:input="handleInput($event)"
                />
                <label :for="`${name}-${option.value}`" class="form-check-label">{{
                    option.label
                }}</label>
                <!-- View mode indicator icons - stacked: beside selected answer -->
                <i
                    v-if="showIndicators && stacked && localValue === option.value && (typeof option.isCorrect === 'boolean' || option.isNA)"
                    class="fi answer-indicator"
                    :class="
                        option.isNA
                            ? 'indicator-na'
                            : option.isCorrect
                            ? 'fi-check indicator-correct'
                            : 'fi-x indicator-incorrect'
                    "
                ></i>
            </div>
        </div>
        <!-- View mode indicator icons - wrapped: at end of row, aligned -->
        <i
            v-if="showIndicators && !stacked && localValue != null && (typeof selectedOption?.isCorrect === 'boolean' || selectedOption?.isNA)"
            class="fi answer-indicator"
            :class="
                selectedOption?.isNA
                    ? 'indicator-na'
                    : selectedOption?.isCorrect
                    ? 'fi-check indicator-correct'
                    : 'fi-x indicator-incorrect'
            "
        ></i>
        <div v-if="isInvalid" class="font-note" style="color: var(--danger)">
            {{ error }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'RadioGroup',
    props: {
        modelValue: {
            type: [String, Number, Boolean],
            default: null,
        },
        options: {
            type: Array,
            required: true,
            validator: (value) =>
                value.every((opt) => 'value' in opt && 'label' in opt),
        },
        name: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        stacked: {
            type: Boolean,
            default: false,
        },
        showIndicators: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isInvalid: false,
        };
    },
    computed: {
        localValue: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
        selectedOption() {
            return this.options.find((opt) => opt.value === this.localValue);
        },
    },
    emits: ['update:modelValue', 'change', 'remove-option'],
    methods: {
        localValueChange() {
            if (!this.disabled) {
                this.$emit('change', this.localValue);
            }
        },
        handleInvalid(event) {
            this.isInvalid = true;
        },
        handleInput(event) {
            this.isInvalid = !event.target.checkValidity();
            this.localValueChange(event.target.value);
        },
        removeOption(optionToRemove) {
            if (this.options.length <= 2) return;
            this.$emit('remove-option', optionToRemove);
        },
    },
};
</script>

<style scoped>
/* Wrapper that holds options + end indicator in a row */
.radio-group-wrapper {
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.radio-group-wrapper.radio-group-stacked {
    flex-direction: column;
    align-items: flex-start;
}

.radio-group {
    display: flex;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
}

.radio-group.radio-group-stacked {
    flex-direction: column;
}

.input-invalid {
    border: 1.5px solid var(--danger);
}

.editable-textarea {
    width: 100%;
    min-height: 0.875rem;
    /* sets the one-line height to match the static label */
    resize: none;
    border: none;
    padding: 0;
    margin: 0;
    outline: none;
    font-size: 0.875rem;
    font-weight: 550;
    line-height: 100%;
    /* match this to how tall your static label appears */
    color: var(--Colours-Neutral-900, #474747);
    background: transparent;
    overflow: hidden;
}

.option-row {
    transition: background-color 0.2s ease;
}

.option-row:hover {
    background-color: var(--neutral-450);
}

.close-icon {
    color: var(--neutral-800);
    transition: transform 0.15s ease, color 0.15s ease;
}

.close-icon:hover {
    color: var(--neutral-900);
    /* slightly darker for emphasis */
}

/* Answer indicator icons for view mode */
.answer-indicator {
    margin-left: 0.375rem;
    font-size: 0.875rem;
}

.indicator-correct {
    color: var(--success);
}

.indicator-incorrect {
    color: var(--danger);
}

.indicator-na {
    color: var(--neutral-800);
    font-family: inherit;
    font-style: normal;
}

.indicator-na::before {
    content: '—';
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 600;
}
</style>
